import { Table as AntdTable, TableProps as AntdTableProps } from "antd";
import { SorterResult, TableRowSelection } from "antd/es/table/interface";
import classNames from "classnames";
import { dragAndDropEnabledAtom } from "components/UI/Table/atoms/dragAndDropAtom.ts";
import { tableAtom } from "components/UI/Table/atoms/tableAtom.ts";
import { useTable } from "components/UI/Table/hooks/useTable.ts";
import { PageSize, TableAction, TableColumnType } from "components/UI/Table/types/table.ts";
import { DnDWrapper } from "components/UI/Table/UI/DragAndDrop/DnDWrapper.tsx";
import { HorizontalTableBodyCell, VerticalTableBodyCell } from "components/UI/Table/UI/DragAndDrop/TableBodyCell";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { usePermission } from "hooks/usePermissions";
import { useEffect } from "react";
import { toast } from "react-toastify";
import { useRecoilValue } from "recoil";
import { PermissionEnum } from "types/enums";
import { DrawerId, Identifiable, NonIdentifiable, TableId } from "types/models.ts";
import { zIndexes } from "utils/zIndexes.ts";

import { ActionButtons } from "./UI/Actions/ActionButtons";
import { DraggableTableRow } from "./UI/DragAndDrop/DraggableTableRow";
import { HorizontalTableHeaderCell, VerticalTableHeaderCell } from "./UI/DragAndDrop/TableHeaderCell";
import { DnDSortingStrategy } from "./UI/DragAndDrop/type";
import { TableRow } from "./UI/Editable/TableRow";
import { TopBar } from "./UI/TopBar/TopBar";
import { getSortParamOnChange } from "./utils/getSortInfo";

import "./overrides.css";

import PaginationResponse = App.Http.Responses.PaginationResponse;

export interface TableProps<
    R extends ResponseWithPagination | ResponseWithoutPagination,
    T extends Identifiable | NonIdentifiable,
> extends Omit<AntdTableProps<T>, "columns" | "dataSource" | "summary"> {
    tableId: TableId;
    data: R;
    dataSource: T[];
    columnConfig: TableColumnType<T>[];
    actions?: TableAction<T>[];
    actionsLabel?: string | null;
    defaultDelete?: (record: T) => Promise<void>;
    // TODO: temporery solution for record type when we wanna change weight
    defaultOnUpdate?: (record: any) => Promise<void>;
    onExport?: () => Promise<string | undefined>;
    enableWordWrap?: boolean;
    enableColumnsVisibility?: boolean;
    enableDragAndDrop?: boolean;
    enableFilters?: boolean;
    addEntityData?: AddEntityData;
    onRefresh?: () => void;
    showRefetchButton?: boolean;
    enableTimeFilter?: boolean;
    sortingStrategy?: DnDSortingStrategy;
    hideActionsColumn?: boolean; // New prop to hide Actions column
    actionsWidth?: number; // Fixed pixel width for the actions column, so it doesn't absorb leftover table-layout: fixed space
    dateFilterDataIndex?: string;
    extraTopBarIcons?: React.ReactNode;
    extraTopBarElements?: React.ReactNode;
    allowExpand?: boolean;
    expandButtonRef?: React.RefObject<() => void>;
    extraFilters?: Array<{
        dataIndex: string;
        title: string;
        filterType: import("./types/filter").FilterType;
        filterOptions?: { label: string; value: string | number | boolean }[];
        filterName?: string;
    }>;
    summary?: (visibleColumns: TableColumnType<T>[] | null) => React.ReactNode;
    autoScrollWidth?: boolean; // Auto-calculate scroll width from column widths
    enableTopBar?: boolean;
    sticky?: AntdTableProps<T>["sticky"];
}

export interface AddEntityData {
    drawerId: DrawerId;
    permission?: PermissionEnum;
}

export interface ResponseWithPagination {
    pagination: PaginationResponse;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ResponseWithoutPagination {}

export const Table = <
    R extends ResponseWithPagination | ResponseWithoutPagination,
    T extends Identifiable | NonIdentifiable,
>({
    tableId,
    data,
    dataSource,
    columnConfig,
    actions,
    actionsLabel,
    defaultDelete,
    defaultOnUpdate,
    enableWordWrap = false,
    enableColumnsVisibility = true,
    enableDragAndDrop = true,
    enableFilters = false,
    pagination,
    addEntityData,
    onRefresh,
    showRefetchButton,
    enableTimeFilter,
    sortingStrategy = "horizontal",
    hideActionsColumn = false,
    actionsWidth,
    scroll,
    onExport,
    dateFilterDataIndex,
    extraTopBarIcons,
    extraTopBarElements,
    allowExpand = false,
    expandButtonRef,
    extraFilters,
    autoScrollWidth = false,
    enableTopBar = true,
    sticky,
    ...props
}: TableProps<R, T>) => {
    const tableUtils = useTable<T>(tableId, dateFilterDataIndex);
    const tableAtomInstance = tableAtom<T>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    // `enableDragAndDrop={false}` must also gate the actual reorder behavior, not just hide the
    // TopBar toggle — otherwise a table with the toggle hidden could still end up draggable (e.g.
    // if the underlying atom is ever set true some other way).
    const dragAndDropEnabled = useRecoilValue(dragAndDropEnabledAtom(tableId)) && enableDragAndDrop;
    const permissionsUtils = usePermission();
    const { t: tC } = usePaldockTranslation("common");
    const { t: tUI } = usePaldockTranslation("tables.ui");

    useEffect(() => {
        tableUtils.setDefaultState(columnConfig, Boolean(enableTimeFilter));
    }, []);

    useEffect(() => {
        if (tableState.originColumns?.length) {
            tableUtils.initializeFiltersFromUrl();
        }
    }, [tableState.originColumns]);

    const isLoading =
        typeof props.loading === "boolean" ? props.loading : (props.loading?.spinning ?? Boolean(props.loading));

    if (!tableState.columns) return null;
    if (!data && !isLoading) return null;

    const handleSave = (record: T) => {
        tableUtils.setNullEditingRowId();
        defaultOnUpdate?.(record);
    };

    const handleCancel = () => {
        tableUtils.setNullEditingRowId();
    };

    // Calculate total width from columns if autoScrollWidth is enabled
    const calculatedScrollWidth = autoScrollWidth ? 10 : undefined;

    const columns = createColumns<T>(
        tableState.columns,
        handleSave,
        handleCancel,
        tableUtils.setEditingRowId,
        tableState.editingRowId,
        permissionsUtils,
        enableWordWrap,
        hideActionsColumn ? undefined : actions,
        actionsLabel ?? tC("action"),
        defaultDelete,
        actionsWidth
    );

    const updateTableDataOrder = async (records: T[]) => {
        const mutationPromises = records.map((record, index) => {
            defaultOnUpdate?.({
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                id: record.id,
                weight: index,
            });
        });

        try {
            await Promise.all(mutationPromises);
            toast.success(`Data byly uložené.`);
        } catch (error) {
            console.log(error);
        }
    };

    const renderTable = () => {
        const hasData = Boolean(data);
        const tableElement = (
            <AntdTable
                dataSource={dataSource}
                components={
                    dragAndDropEnabled
                        ? {
                              header: {
                                  cell:
                                      sortingStrategy === "vertical"
                                          ? VerticalTableHeaderCell
                                          : HorizontalTableHeaderCell,
                              },
                              body: {
                                  cell:
                                      sortingStrategy === "vertical" ? VerticalTableBodyCell : HorizontalTableBodyCell,
                                  row: sortingStrategy === "vertical" ? DraggableTableRow : TableRow,
                              },
                          }
                        : {
                              body: {
                                  row: TableRow,
                              },
                          }
                }
                columns={columns}
                locale={{ emptyText: tUI("empty-text") }}
                scroll={
                    scroll ??
                    (autoScrollWidth && calculatedScrollWidth
                        ? { x: calculatedScrollWidth, y: "calc(100vh - 300px)" }
                        : { x: "max-content", y: "calc(100vh - 300px)" })
                }
                pagination={
                    pagination == false
                        ? false
                        : pagination === undefined && hasData && isResponseWithPagination(data)
                          ? {
                                className: "sticky bottom-0 bg-white rounded",
                                style: { zIndex: zIndexes.tablePagination },
                                showSizeChanger: true,
                                pageSizeOptions: [PageSize.Five, PageSize.Ten, PageSize.Thirty, PageSize.OneHundred],
                                current: data.pagination.currentPage,
                                pageSize: data.pagination.perPage,
                                total: data.pagination.total,
                                showTotal: (total, range) =>
                                    tUI("pagination.showTotal", { from: range[0], to: range[1], total }),
                            }
                          : pagination
                }
                onChange={(paginationArg, _, sorterArg: SorterResult<T> | SorterResult<T>[]) => {
                    // --- SORT ---
                    const s = Array.isArray(sorterArg) ? sorterArg[0] : sorterArg;
                    const sortParam = s?.order ? getSortParamOnChange(s) : undefined;
                    const isReset = s?.order === undefined;

                    tableUtils.saveSort(sortParam, isReset);

                    // --- PAGINATION ---
                    if (paginationArg && hasData && isResponseWithPagination(data)) {
                        const { current, pageSize } = paginationArg;
                        if (current !== data.pagination.currentPage || pageSize !== data.pagination.perPage) {
                            tableUtils.savePage({ number: current, size: pageSize });
                        }
                    }
                }}
                className={"p-0"}
                bordered={true}
                sticky={
                    enableTopBar
                        ? { offsetSummary: 64, offsetHeader: 46, ...(typeof sticky === "object" && sticky) }
                        : false
                }
                {...props}
                summary={props.summary ? () => props.summary!(tableState.columns) : undefined}
            />
        );

        return dragAndDropEnabled ? (
            <DnDWrapper<T>
                tableId={tableId}
                sortingStrategy={sortingStrategy}
                dataSource={dataSource}
                onReorder={(newData) => updateTableDataOrder(newData as T[])}
            >
                {tableElement}
            </DnDWrapper>
        ) : (
            tableElement
        );
    };

    return (
        <div className="bg-white rounded">
            {enableTopBar && (enableFilters || showRefetchButton || enableColumnsVisibility || enableTimeFilter) && (
                <TopBar<T>
                    enableFilters={enableFilters}
                    columnConfig={columnConfig}
                    tableId={tableId}
                    addEntityData={addEntityData}
                    enableColumnsVisibility={enableColumnsVisibility}
                    enableDragAndDrop={enableDragAndDrop}
                    onRefresh={onRefresh}
                    showRefetchButton={showRefetchButton}
                    enableTimeFilter={enableTimeFilter}
                    loading={props.loading}
                    onExport={onExport}
                    extraFilters={extraFilters}
                    dateFilterDataIndex={dateFilterDataIndex}
                    extraIcons={extraTopBarIcons}
                    extraButtons={extraTopBarElements}
                    renderTable={renderTable}
                    allowExpand={allowExpand}
                    expandButtonRef={expandButtonRef}
                />
            )}
            {renderTable()}
        </div>
    );
};

const createColumns = <T extends Identifiable>(
    columns: TableColumnType<T>[],
    handleSave: (record: T) => void,
    handleCancel: (record: T) => void,
    handleEdit: (record: T) => void,
    editingRowId: string | null,
    permissionsUtils: ReturnType<typeof usePermission>,
    enableWordWrap?: boolean,
    actions?: TableAction<T>[],
    actionsLabel?: string | null,
    defaultDelete?: (record: T) => Promise<void>,
    actionsWidth?: number
) => {
    const mappedColumns = columns.map((col) => ({
        ...col,
        className: classNames(col.className, !enableWordWrap && "whitespace-nowrap"),
    }));

    if (!actions || actions.length === 0) {
        return mappedColumns;
    }

    return [
        ...mappedColumns,
        {
            title: actionsLabel === null ? "" : actionsLabel,
            key: "actions",
            dataIndex: "actions",
            fixed: "right" as TableRowSelection<T>["fixed"],
            editable: false,
            className: "!bg-white",
            ...(actionsWidth !== undefined ? { width: actionsWidth } : {}),
            render: (_: unknown, record: T) => (
                <ActionButtons<T>
                    actions={actions.filter(
                        (action) => !action.permission || permissionsUtils.hasPermission(action.permission)
                    )}
                    record={record}
                    defaultDelete={defaultDelete}
                    handleSave={handleSave}
                    handleCancel={handleCancel}
                    handleEdit={handleEdit}
                    editing={record.id !== undefined ? record.id === editingRowId : false}
                />
            ),
        },
    ];
};

const isResponseWithPagination = (
    data: ResponseWithPagination | ResponseWithoutPagination
): data is ResponseWithPagination => {
    return (data as ResponseWithPagination).pagination !== undefined;
};
